// Phase C-Lite (Jul-2026) — Project Reporting Period ("YYYY-MM").
// Management-only month used by the Project Monthly Performance P&L.
// It NEVER changes accounting/GST dates, amounts or approvals.
import { useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, X } from "lucide-react";

export const RP_NOTE = "Management reporting only — does not change accounting/GST date.";

const MONTHS = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
  may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8,
  sep: 9, sept: 9, september: 9, oct: 10, october: 10, nov: 11, november: 11,
  dec: 12, december: 12,
};
const PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

export function monthFromDate(dateStr) {
  const p = (dateStr || "").slice(0, 7);
  return PERIOD_RE.test(p) ? p : "";
}

// Conservative parser mirroring backend services/reporting_period.parse_period_text:
// only unambiguous single-month values ("Apr-26", "March 2026", "2026-04") resolve.
export function parsePeriodText(raw) {
  if (!raw || typeof raw !== "string") return "";
  const s = raw.trim();
  if (PERIOD_RE.test(s)) return s;
  const m = s.match(/^([a-zA-Z]{3,9})[\s\-./]*(\d{2}|\d{4})$/);
  if (!m) return "";
  const mon = MONTHS[m[1].toLowerCase()];
  if (!mon) return "";
  let yr = parseInt(m[2], 10);
  if (yr < 100) yr += 2000;
  if (yr < 2000 || yr > 2099) return "";
  return `${yr}-${String(mon).padStart(2, "0")}`;
}

export function RpNote() {
  return <div className="text-[9px] text-slate-400 leading-tight mt-0.5">{RP_NOTE}</div>;
}

export function ReportingPeriodInput({ value, onChange, testid, className, disabled, showNote = true }) {
  return (
    <>
      <input
        type="month"
        value={value || ""}
        disabled={disabled}
        title={RP_NOTE}
        onChange={e => onChange(e.target.value)}
        data-testid={testid || "reporting-period-input"}
        className={className}
      />
      {showNote && <RpNote />}
    </>
  );
}

export function ReportingPeriodEditDialog({ resource, docId, lineIndex, current, voucherLabel, onClose, onSaved }) {
  const [period, setPeriod] = useState(current || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!reason.trim()) { toast.error("A reason is mandatory"); return; }
    setBusy(true);
    try {
      const body = { reporting_period: period, reason: reason.trim() };
      if (lineIndex !== undefined && lineIndex !== null) body.line_index = lineIndex;
      const r = await api.patch(`/${resource}/${docId}/reporting-period`, body);
      toast.success("Reporting Period updated");
      onSaved?.(period, r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" data-testid="reporting-period-edit-dialog">
      <div className="bg-white w-full max-w-md shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold">Change Project Reporting Period</h3>
          <button onClick={onClose} data-testid="rp-dialog-close"><X size={16} /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          {voucherLabel && <div className="text-xs font-bold text-slate-700">{voucherLabel}</div>}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Current</div>
            <div className="text-sm tabular text-slate-800" data-testid="rp-dialog-current">
              {current || "— (accounting-month fallback)"}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-600">New Reporting Period</label>
            <input
              type="month" value={period}
              onChange={e => setPeriod(e.target.value)}
              data-testid="rp-dialog-period"
              className="w-full px-3 py-2 text-sm border border-slate-300 focus:border-[#F97316] focus:outline-none"
            />
            <RpNote />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Reason *</label>
            <textarea
              rows={2} value={reason}
              onChange={e => setReason(e.target.value)}
              data-testid="rp-dialog-reason"
              placeholder="Why is the reporting month being corrected? (saved to an append-only audit history)"
              className="w-full px-3 py-2 text-sm border border-slate-300 focus:border-[#F97316] focus:outline-none resize-y"
            />
          </div>
          <div className="text-[10px] text-slate-500 bg-slate-50 border border-slate-200 px-2.5 py-1.5">
            Voucher amounts, GST, approval status and accounting dates stay untouched. Requires the
            &quot;Edit Reporting Period (Post-Approval)&quot; permission on the Performance P&amp;L module.
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900">Cancel</button>
          <button
            onClick={save} disabled={busy} data-testid="rp-dialog-save"
            className="px-5 py-2 text-xs font-bold uppercase tracking-wider bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-60 text-white"
          >
            {busy ? "Saving…" : "Save Change"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function ApprovedReportingPeriod({ resource, docId, lineIndex, value, onSaved, testid, className, voucherLabel, showNote = true }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="flex items-center gap-1.5">
        <input
          readOnly value={value || ""} placeholder="— fallback —" title={RP_NOTE}
          data-testid={testid || "reporting-period-readonly"}
          className={`${className || ""} bg-slate-50 text-slate-600`}
        />
        <button
          type="button" onClick={() => setOpen(true)}
          title="Change Reporting Period (post-approval, audited)"
          data-testid={testid ? `${testid}-edit-btn` : "reporting-period-edit-btn"}
          className="text-slate-400 hover:text-[#F97316] p-1 shrink-0"
        ><Pencil size={12} /></button>
      </div>
      {showNote && <RpNote />}
      {open && (
        <ReportingPeriodEditDialog
          resource={resource} docId={docId} lineIndex={lineIndex} current={value} voucherLabel={voucherLabel}
          onClose={() => setOpen(false)}
          onSaved={(p) => { setOpen(false); onSaved?.(p); }}
        />
      )}
    </>
  );
}
